<template>
  <v-row class="mx-0">
    <v-col :md="vShowCalendar ? 4 : 12" class="py-0">
      <v-row>
        <v-col :md="vShowCalendar ? 12 : 8">
          <v-row class="custom-grey-border">
            <v-col
              md="12"
              class="light-gray-background custom-border-bottom d-flex justify-space-between"
            >
              <div
                style="font-size: 19px"
                class="text-uppercase font-weight-700 color-custom-blue"
              >
                Schedule
              </div>
              <div>
                <v-btn
                  class="mx-2 custom-bold-button white--text"
                  color="cyan"
                  right
                  small
                  :disabled="jobLoading"
                  depressed
                  v-on:click="updateCalendar()"
                >
                  <v-icon small class="mr-2">mdi-calendar-blank</v-icon>
                  <span v-if="vShowCalendar">Hide Calendar</span>
                  <span v-else>Show Calendar</span>
                </v-btn>
              </div>
            </v-col>
            <v-col :md="vShowCalendar ? 12 : 3">
              <label class="ml-2" for="visit-one-off-start-date"
                >Start Date</label
              >
              <DatePicker
                :allowed-dates="allowedDates"
                :disabled="jobLoading"
                key="visit-one-off-start-date"
                id="visit-one-off-start-date"
                placeholder="Start Date"
                :value="oneOffSchedule.start_date"
                v-on:change="
                  updateScheduleState('dbOneOffSchedule.start_date', $event)
                "
              />
            </v-col>

            <v-col :md="vShowCalendar ? 12 : 3">
              <label class="ml-2" for="visit-one-off-start-time"
                >Start Time</label
              >
              <TimePicker
                :disabled="jobLoading"
                key="visit-one-off-start-time"
                id="visit-one-off-start-time"
                placeholder="Start Time"
                :value="oneOffSchedule.start_time"
                v-on:change="
                  updateScheduleState('dbOneOffSchedule.start_time', $event)
                "
                type="start-time"
              />
            </v-col>
            <v-col :md="vShowCalendar ? 12 : 3">
              <label class="ml-2" for="visit-one-off-end-date">End Date</label>
              <DatePicker
                :allowed-dates="allowedDates"
                :disabled="jobLoading"
                key="visit-one-off-end-date"
                id="visit-one-off-end-date"
                placeholder="End Date"
                :min-date="oneOffSchedule.start_date"
                :value="oneOffSchedule.end_date"
                v-on:change="
                  updateScheduleState('dbOneOffSchedule.end_date', $event)
                "
              />
            </v-col>
            <v-col :md="vShowCalendar ? 12 : 3">
              <label class="ml-2" for="visit-one-off-end-time">End Time</label>
              <TimePicker
                :disabled="jobLoading"
                key="visit-one-off-end-time"
                id="visit-one-off-end-time"
                placeholder="End Time"
                :min-time="oneOffSchedule.start_time"
                :min-date="oneOffSchedule.end_date"
                :min-date-reference="oneOffSchedule.start_date"
                :value="oneOffSchedule.end_time"
                v-on:change="
                  updateScheduleState('dbOneOffSchedule.end_time', $event)
                "
                type="end-time"
              />
            </v-col>
            <v-col md="12">
              <p
                class="m-0 p-4 font-size-16 font-weight-500 light-gray-background custom-grey-border"
              >
                <span
                  v-if="hasPublicHoliday"
                  class="d-flex align-center mb-2 color-custom-blue"
                  role="status"
                >
                  <v-icon color="amber darken-2" class="mr-2"
                    >mdi-alert-outline</v-icon
                  >
                  Job period includes a Public Holiday
                </span>
                <span class="d-block">Job Duration: {{ jobDuration }}</span>
                <span class="d-block mt-2">
                  This job will start on
                  {{ formatDate(oneOffSchedule.start_date) }}
                  {{ oneOffSchedule.start_time }} and finish on
                  {{ formatDate(oneOffSchedule.end_date) }}
                  {{ oneOffSchedule.end_time }}
                </span>
              </p>
            </v-col>
          </v-row>
        </v-col>
        <v-col :md="vShowCalendar ? 12 : 4">
          <ScheduleTeam
            :key="`job-one-off-schedule-team-${scheduleKey}`"
            :selectedCustomer="this.vCustomerId"
          />
        </v-col>
      </v-row>
    </v-col>
    <v-col v-if="vShowCalendar" md="8">
      <Calendar
        :key="`job-one-off-calendar-${scheduleKey}`"
        :show-calendar="vShowCalendar"
      ></Calendar>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters } from "vuex";
import ScheduleTeam from "@/view/components/main-job/ScheduleTeam.vue";
import DatePicker from "@/view/components/main-job/QDatePicker.vue";
import TimePicker from "@/view/components/main-job/TimePicker.vue";
import Calendar from "@/view/pages/partials/Calendar.vue";
import { QUERY } from "@/core/services/store/request.module";
import {
  UPDATE_DEFAULT_DATE,
  UPDATE_CALENDAR_ACTION,
  UPDATE_SCHEDULE_STATE,
  // RESET_ENGINEER,t
} from "@/core/services/store/job.module";
import moment from "moment-timezone";

moment.tz.setDefault(process.env.VUE_APP_TIMEZONE);

export default {
  name: "job-ctx-one-off-schedule",
  props: {
    vCustomerId: {
      type: Number,
      required: false,
    },
  },
  data() {
    return {
      todayDate: moment(new Date()).format("YYYY-MM-DD"),
      scheduleKey: Number(new Date()),
      holidaysByYear: {},
      holidayRequests: {},
    };
  },
  watch: {
    "oneOffSchedule.start_date": "loadScheduleHolidays",
    "oneOffSchedule.end_date": "loadScheduleHolidays",
  },
  methods: {
    allowedDates(val) {
      const date = new Date(val);
      return date.getDay() !== 0; // Allow only non-Sundays
    },
    formatDate(date) {
      if (moment(date).isValid()) {
        return moment(date).format("DD MMMM YYYY");
      }
    },
    loadScheduleHolidays() {
      const startDate = moment(this.oneOffSchedule.start_date, "YYYY-MM-DD", true);
      const endDate = moment(this.oneOffSchedule.end_date, "YYYY-MM-DD", true);

      if (!startDate.isValid() || !endDate.isValid()) {
        return;
      }

      const years = [];
      const yearCursor = startDate.clone().startOf("year");
      const lastYear = endDate.clone().startOf("year");

      while (yearCursor.isSameOrBefore(lastYear, "year")) {
        years.push(yearCursor.format("YYYY"));
        yearCursor.add(1, "year");
      }

      years.forEach((year) => {
        if (this.holidaysByYear[year] || this.holidayRequests[year]) {
          return;
        }

        this.$set(this.holidayRequests, year, true);
        this.$store
          .dispatch(QUERY, { url: "holiday", data: { year } })
          .then(({ data }) => {
            this.$set(this.holidaysByYear, year, Array.isArray(data) ? data : []);
          })
          .catch(() => {
            // Holiday lookup is supplemental to scheduling; keep the form usable.
            this.$set(this.holidaysByYear, year, []);
          })
          .finally(() => {
            this.$delete(this.holidayRequests, year);
          });
      });
    },
    updateScheduleState(key, value) {
      // If updating start date, ensure end date is not before start date
      if (key === "dbOneOffSchedule.start_date") {
        const currentEndDate = moment(this.oneOffSchedule.end_date);
        const newStartDate = moment(value);
        if (currentEndDate.isBefore(newStartDate)) {
          this.$store.commit(UPDATE_SCHEDULE_STATE, {
            key: "dbOneOffSchedule.end_date",
            value: value,
          });
        }
      }

      this.$store.commit(UPDATE_SCHEDULE_STATE, { key, value });
      // this.$store.commit(RESET_ENGINEER);
      this.updateDefaultDate();
    },
    updateDefaultDate() {
      this.$nextTick(() => {
        const startDateTime = `${this.oneOffSchedule.start_date} ${this.oneOffSchedule.start_time}`;
        const endDateTime = `${this.oneOffSchedule.end_date} ${this.oneOffSchedule.end_time}`;

        this.$store.dispatch(UPDATE_DEFAULT_DATE, {
          startedAt: startDateTime,
          finishedAt: endDateTime,
        });
      });
    },
    updateCalendar() {
      this.$store.commit(UPDATE_CALENDAR_ACTION);
    },
  },
  mounted() {
    this.loadScheduleHolidays();
    this.updateDefaultDate();
  },
  components: {
    ScheduleTeam,
    Calendar,
    TimePicker,
    DatePicker,
  },
  computed: {
    jobDuration() {
      const start = moment(
        `${this.oneOffSchedule.start_date} ${this.oneOffSchedule.start_time}`,
        "YYYY-MM-DD hh:mm A",
        true
      );
      const end = moment(
        `${this.oneOffSchedule.end_date} ${this.oneOffSchedule.end_time}`,
        "YYYY-MM-DD hh:mm A",
        true
      );

      if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
        return "0 Hour(s)";
      }

      const totalMinutes = end.diff(start, "minutes");
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (!minutes) {
        return `${hours} Hour(s)`;
      }

      if (!hours) {
        return `${minutes} Minute(s)`;
      }

      return `${hours} Hour(s) ${minutes} Minute(s)`;
    },
    hasPublicHoliday() {
      const startDate = moment(this.oneOffSchedule.start_date, "YYYY-MM-DD", true);
      const endDate = moment(this.oneOffSchedule.end_date, "YYYY-MM-DD", true);

      if (!startDate.isValid() || !endDate.isValid() || endDate.isBefore(startDate)) {
        return false;
      }

      return Object.keys(this.holidaysByYear).some((year) =>
        this.holidaysByYear[year].some((holiday) => {
          const holidayDate = moment(holiday.date).format("YYYY-MM-DD");
          return (
            holidayDate >= startDate.format("YYYY-MM-DD") &&
            holidayDate <= endDate.format("YYYY-MM-DD")
          );
        })
      );
    },
    ...mapGetters(["vShowCalendar", "oneOffSchedule", "jobLoading"]),
  },
};
</script>
